import React from 'react';
import { useRouteMatch } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { Button, Checkbox, List, Form, Input } from 'antd';

import { useQuotationContext } from '..';

import {
  useQuotationConfiguration,
  useQuotationOffersConfiturationSend,
} from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.hooks';
import { SendQuotationOffersRequest } from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

interface ConfigurationValues {
  supplierId: number;
  itemId: number;
}

type FormValues = Record<
  string,
  {
    configuration: (ConfigurationValues & {
      configured: boolean;
    })[];
  }
>;

const QuotationDetailsOffersConfigurationPage = () => {
  const [form] = Form.useForm<FormValues>();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const quotation = useQuotationContext();

  const { data, isFetching } = useQuotationConfiguration({
    quotationId: Number(quotationId),
  });

  const configuration = flatInfiniteResult(data);

  const saveMutation = useQuotationOffersConfiturationSend();

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      let configuration = [] as ConfigurationValues[];

      Object.keys(values).forEach((key: string) => {
        const keyItem = values[key];

        keyItem.configuration.map(({ supplierId, itemId, configured }) => {
          if (configured) {
            configuration = [...configuration, { itemId, supplierId }];
          }
        });
      });

      const obj: SendQuotationOffersRequest = {
        quotationId: Number(quotationId),
        configuration,
      };

      await saveMutation.mutateAsync(obj);
    } catch {}
  };

  return (
    <div>
      <div className="create-page-title">
        <SpaceForButton justifyContent="space-between">
          <h2 style={{ margin: 0 }}>
            <FormattedMessage id="quotations.details.offersConfiguration" />
          </h2>
          <div>
            <Button
              block
              type="primary"
              onClick={handleSaveClick}
              loading={saveMutation.isLoading}
              disabled={quotation?.disabled}
            >
              <FormattedMessage id="quotations.details.offersConfiguration.save" />
            </Button>
          </div>
        </SpaceForButton>
      </div>
      <Form form={form}>
        <List
          loading={isFetching}
          dataSource={configuration}
          renderItem={({ item, suppliers }, i) => (
            <List.Item>
              <List.Item.Meta
                title={`${
                  item.manufacturingCode ? item.manufacturingCode + ' - ' : ''
                }${item.name}`}
                description={suppliers.map((s, n) => (
                  <div key={s.supplier.supplierId}>
                    <Form.Item
                      hidden
                      name={[i, 'configuration', n, 'supplierId']}
                      initialValue={s.supplier.supplierId}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      hidden
                      name={[i, 'configuration', n, 'itemId']}
                      initialValue={item.id}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      noStyle
                      style={{ margin: 0 }}
                      name={[i, 'configuration', n, 'configured']}
                      valuePropName="checked"
                      initialValue={s.configured}
                    >
                      <Checkbox disabled={quotation?.disabled}>
                        {s.supplier.name}
                      </Checkbox>
                    </Form.Item>
                  </div>
                ))}
              />
            </List.Item>
          )}
        />
      </Form>
    </div>
  );
};

export default QuotationDetailsOffersConfigurationPage;
